import React, { useEffect } from 'react';
import { Box, Heading, Tabs } from '@wix/design-system';
import { horizontalNavBarDataHooks } from './data-hooks';

/** A nav bar item, in the shape the WDS Tabs renders - arrives render-ready
 *  from the producing service; this component only feeds it through. */
export interface HorizontalNavBarItem {
  id: string | number;
  title: React.ReactNode;
}

interface HorizontalNavBarBaseProps {
  items: HorizontalNavBarItem[];
  selectedItemKey?: string;
  onItemClick(item: HorizontalNavBarItem, index: number): void;
}

interface PillsNavBarProps extends HorizontalNavBarBaseProps {
  skin?: 'pills';
  sideContent?: React.ReactNode;
}

interface TabsNavBarProps extends HorizontalNavBarBaseProps {
  skin: 'tabs';
  title: string;
}

export type HorizontalNavBarProps = PillsNavBarProps | TabsNavBarProps;

export function HorizontalNavBar(props: HorizontalNavBarProps) {
  if (props.skin === 'tabs') {
    return <TabsNavBar {...props} />;
  }
  return <PillsNavBar {...props} />;
}

function TabsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  title,
}: TabsNavBarProps) {
  return (
    <Box width="100%" gap="24px" verticalAlign="middle">
      <Heading size="extraLarge" dataHook={horizontalNavBarDataHooks.title}>
        {title}
      </Heading>
      <Tabs
        items={items}
        activeId={selectedItemKey}
        onClick={(item) => onItemClick(item, items.indexOf(item))}
        size="medium"
        showDivider={false}
        horizontalPadding={false}
      />
    </Box>
  );
}

function PillsNavBar({
  items,
  selectedItemKey,
  onItemClick,
  sideContent,
}: PillsNavBarProps) {
  /* items overflow into horizontal scroll - keep the selected one visible;
     centered, so it clears the scroll arrows overlaying the edges */
  useEffect(() => {
    const selectedItem = document.querySelector(
      `[data-hook="${horizontalNavBarDataHooks.root}"] [role="tab"][aria-selected="true"]`,
    );
    selectedItem?.scrollIntoView?.({ block: 'nearest', inline: 'center' });
  }, [selectedItemKey]);

  return (
    <Tabs
      dataHook={horizontalNavBarDataHooks.root}
      items={items}
      activeId={selectedItemKey}
      onClick={(item) => onItemClick(item, items.indexOf(item))}
      size="small"
      showDivider={false}
      horizontalPadding={false}
      scrollOnOverflow
      sideContent={
        sideContent ? (
          <Box dataHook={horizontalNavBarDataHooks.sideContent}>
            {sideContent}
          </Box>
        ) : undefined
      }
    />
  );
}
